import React from "react";
import { useHistory } from "react-router-dom";
import { Button, Typography } from "@material-ui/core";
import DataTable from "@/components/DataTable";
import { ReactRender } from "@/components/ReactRender";
import ChipStatus from "@/components/ChipStatus";
import HasPermission from "@/components/commons/HasPermission";
import { useTranslation } from "@/langs/useTranslation";
import { getAlarmHistoryListApi } from "@/apis/apiCollection";
import { formatDate } from "./datetimeHandlers";
import { renderAlarmPolicy } from "./utils";

export const AlarmHistoryListDatatable = React.forwardRef(
  (
    {
      className,
      style,
      getSearchParams,
      footer,
      children,
      onCheckAll,
      onLoaded,
      onReset,
      onDelete
    },
    ref
  ) => {
    const { t } = useTranslation();
    const history = useHistory();

    const toDetail = e => {
      e.preventDefault();
      const url = new URL(e.target.href);
      history.push(url.pathname);
    };

    const columns = [
      {
        data: "id",
        type: "checkbox"
      },
      {
        data: "deviceSerialNumber",
        title: t({ defaultMessage: "设备SN" }),
        createdCell: (td, data, rowData) => {
          ReactRender(
            <HasPermission permission="Device Monitoring Detail">
              {yes =>
                yes ? (
                  <a
                    href={`/device-list/device-detail/${rowData.deviceId}/${
                      rowData.os_type || "android"
                    }`}
                    onClick={toDetail}
                  >
                    {data}
                  </a>
                ) : (
                  data
                )
              }
            </HasPermission>,
            td
          );
        }
      },
      {
        name: "deviceName",
        data: "deviceName",
        orderable: false,
        title: t({ defaultMessage: "设备名称" })
      },
      {
        name: "deviceDetail",
        data: "deviceDetail",
        orderable: false,
        title: t({ defaultMessage: "描述" })
      },
      {
        data: "alarmTemplateName",
        title: `${t({ defaultMessage: "报警模板名称" })}`
      },
      {
        data: "alarmPolicyOptionName",
        orderable: false,
        title: `${t({ defaultMessage: "报警规则" })}`,
        render: (data, type, row) => {
          return renderAlarmPolicy(data, row);
        }
      },
      {
        data: "groups",
        orderable: false,
        title: `${t({ defaultMessage: "门店" })}`,
        render: data => {
          return data?.map(item => item.name).join(", ");
        }
      },
      {
        data: "tags",
        orderable: false,
        title: t({ defaultMessage: "标签" }),
        render: data => {
          return data?.map(item => item.name).join(", ");
        }
      },
      {
        data: "status",
        orderable: false,
        title: t({ defaultMessage: "处理状态" }),
        createdCell: (td, data) => {
          ReactRender(
            <ChipStatus
              size="small"
              type={data === "resetting" ? "success" : "warn"}
              label={
                data === "resetting"
                  ? t({ defaultMessage: "已处理" })
                  : t({ defaultMessage: "未处理" })
              }
            />,
            td
          );
        }
      },
      {
        data: "triggerValue",
        orderable: false,
        title: t({ defaultMessage: "触发值" }),
        className: "center",
        createdCell: (td, data) => {
          ReactRender(
            <Typography variant="body2" color="primary">
              {data}
            </Typography>,
            td
          );
        }
      },
      {
        data: "triggerDate",
        title: t({ defaultMessage: "触发时间" }),
        searchable: false,
        render: data => {
          return formatDate(data);
        }
      },
      {
        data: null,
        title: t({ defaultMessage: "操作" }),
        className: "center nowrap",
        searchable: false,
        orderable: false,
        createdCell: (td, data, row) => {
          ReactRender(
            <HasPermission permission="alarm_history_alarm_delete">
              <Button size="small" color="primary" onClick={() => onReset(row)}>
                {data.alarmPolicyOptionName === "App Crash Detection"
                  ? t({ defaultMessage: "重启" })
                  : t({ defaultMessage: "重置" })}
              </Button>
              <Button
                size="small"
                color="secondary"
                onClick={() => onDelete(row)}
              >
                {t({ defaultMessage: "删除" })}
              </Button>
            </HasPermission>,
            td
          );
        }
      }
    ];

    const ajax = (data, callback) => {
      let orderType;
      let orderWay;
      const order = data.order[0];
      if (order) {
        // orderType = data.columns[order.column]?.name;
        orderType = order.column;
        orderWay = order.dir === "asc" ? 1 : 2;
      }
      return getAlarmHistoryListApi({
        // ...data,
        orderType,
        orderWay,
        ...getSearchParams(),
        page: data.page,
        pageSize: data.pageSize
      }).then(res => {
        callback({
          data: res.alarmHistories,
          draw: data.draw,
          recordsFiltered: res.total,
          recordsTotal: res.total
        });
      });
    };

    return (
      <DataTable
        ref={ref}
        ordering
        options={{ order: [[1, "asc"]] }}
        columns={columns}
        ajax={ajax}
        className={className}
        style={style}
        footer={footer}
        scrollable
        showCheckAll={false}
        onCheckAll={onCheckAll}
        onLoaded={onLoaded}
      >
        {children}
      </DataTable>
    );
  }
);
